import {
  Alert,
  Button,
  closeDialog,
  Form,
  FormButton,
  FormField,
  openConfirmDialog,
  showNotification,
  type ValidationErrors,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {isNil} from 'ramda';

import {
  type ConfigurationDetailResponseBody,
  isNewVehicleConflict,
  newVehicleApi,
  useCommitConfigurationToBusinessCaseMutation,
  useGetConfigurationDetailQuery,
  useOrderConfigurationMutation,
  useRepriceConfigurationMutation,
} from '@dms/api/new-vehicle';
import {businessCaseApi, useGetOfferAlternativesQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes, vehiclesRoutes} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {composePath, type ApiError, useNavigate, useOnMount} from 'shared/web';

import {getFactoryReferenceInUseMessage} from '../../utils/getFactoryReferenceInUseMessage';
import {importerOrderRefSchema} from '../../utils/importerOrderRefSchema';
import {FactoryReferenceOwnerLink} from '../FactoryReferenceOwnerLink/FactoryReferenceOwnerLink';
import {SelectableCard} from '../SelectableCard/SelectableCard';
import {type CommitFailure, getCommitFailure} from './getCommitFailure';
import {getCommitFailureAlert} from './getCommitFailureAlert';

export type OrderConfigurationDialogProps = {
  configuration: ConfigurationDetailResponseBody;
  /**
   * Business Case to commit into when the configuration carries none of its own. The backend
   * adopts it (and backfills a missing customer) on commit; a configuration that is already
   * linked keeps its own Business Case, so a mismatch still fails as DMS-NV-CF-0018.
   */
  targetBusinessCaseId?: string;
  /**
   * Replaces the Business Case's current sale vehicle in the same commit instead of requiring it
   * to be removed first. Needs the Business Case change-vehicle permission on top of ordering.
   */
  replacement?: SaleVehicleReplacement;
  dialogId: string;
  onDone?: VoidFunction;
};

export type SaleVehicleReplacement = {
  /**
   * The sale vehicle observed when the user chose to replace it, or null for an empty slot. The
   * backend compares it with the current selection and answers 409 when it moved underneath.
   */
  expectedSaleVehicleId: string | null;
};

type Branch = 'contract' | 'stock';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

const stockSchema = z.object({
  riskConfirmed: z.boolean().refine((confirmed) => confirmed, {
    message: t('entity.newVehicle.order.riskConfirmationRequired', 'Confirm the dealer stock risk'),
  }),
  importerOrderRef: importerOrderRefSchema,
  orderedOn: z.string().nullable(),
  eta: z.string().nullable(),
  specFreezeDate: z.string().nullable(),
});

type StockValues = z.infer<typeof stockSchema>;

const contractSchema = z.object({
  importerOrderRef: importerOrderRefSchema,
});

type ContractValues = z.infer<typeof contractSchema>;
type SetErrors = (errors: ValidationErrors | null) => void;

const toImporterOrderRef = (value: string) => value.trim() || null;
const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null;

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function OrderConfigurationDialog(props: OrderConfigurationDialogProps) {
  const navigate = useNavigate();
  const isMounted = useRef(true);
  useOnMount(
    () => {
      isMounted.current = true;
    },
    () => {
      isMounted.current = false;
    }
  );
  const dispatch = useDispatch();
  const [canOrder, canWrite] = usePermissions({
    permissionKeys: ['newVehicleConfigurationOrder', 'newVehicleConfigurationWrite'],
  });
  const [branch, setBranch] = useState<Branch>(
    props.configuration.configuration.businessCaseId || props.targetBusinessCaseId
      ? 'contract'
      : 'stock'
  );
  const [failure, setFailure] = useState<CommitFailure>();
  const [ownerVehicleId, setOwnerVehicleId] = useState<string>();
  const [repriceConflictVersion, setRepriceConflictVersion] = useState<number>();
  const configurationId = props.configuration.configuration.id;
  const {dialogId} = props;
  const detailQuery = useGetConfigurationDetailQuery({configurationId});
  const detail = detailQuery.currentData ?? props.configuration;
  const [commit, commitState] = useCommitConfigurationToBusinessCaseMutation();
  const [order, orderState] = useOrderConfigurationMutation();
  const [reprice, repriceState] = useRepriceConfigurationMutation();
  const businessCaseId = detail.configuration.businessCaseId ?? props.targetBusinessCaseId ?? null;
  // The customer's current choice in the online offer: committing another configuration over it
  // is allowed but asked first, like "Use for checkout" on the Selling section. Every host of this
  // dialog is behind the new-vehicle catalogue flag already.
  const {data: offerAlternatives} = useGetOfferAlternativesQuery(
    {businessCaseId: businessCaseId ?? ''},
    {skip: !businessCaseId}
  );
  const customerChoice = offerAlternatives?.customerChoice;
  const chosenAlternative = customerChoice?.isCurrent
    ? offerAlternatives?.alternatives.find(
        (alternative) => alternative.id === customerChoice.alternativeId
      )
    : undefined;
  const isExpired = detail.priceValidity === 'expired';
  const catalogueChanged = detail.catalogueChanged;
  const orderBlocked =
    !canOrder || isExpired || catalogueChanged || detail.configuration.vehicleId !== null;
  const orderPermissionReason = t(
    'entity.newVehicle.order.orderPermissionRequired',
    'You do not have permission to order configurations'
  );
  const reviewConfiguration = () => {
    if (!canWrite) {
      return;
    }
    closeDialog(dialogId);
    navigate(
      composePath(vehiclesRoutes.newVehicleConfigurator, {
        params: {step: '5'},
        queryParams: {
          configurationId,
          businessCaseId: businessCaseId ?? undefined,
          replaceSaleVehicleId: props.replacement?.expectedSaleVehicleId ?? undefined,
          interestId: detail.configuration.interestId ?? undefined,
        },
      })
    );
  };

  const refreshBusinessCase = (id: string) =>
    dispatch(
      businessCaseApi.util.invalidateTags([
        {type: 'BusinessCaseDetail', id},
        {type: 'BusinessCaseActions', id},
        'offer',
      ])
    );

  const onSuccess = (vehicleId: string, committedBusinessCaseId?: string) => {
    if (committedBusinessCaseId) {
      refreshBusinessCase(committedBusinessCaseId);
    }
    if (isMounted.current) {
      closeDialog(dialogId);
    }
    props.onDone?.();
    showNotification.success(
      t('entity.newVehicle.notifications.orderCreated', 'Vehicle order created'),
      {
        layout: 'detailed',
        actions: [
          {
            title: t('entity.newVehicle.actions.openVehicle', 'Open vehicle'),
            onClick: () =>
              navigate(composePath(vehiclesRoutes.newVehicleDetail, {params: {id: vehicleId}})),
          },
        ],
      }
    );
  };

  const selectBranch = (nextBranch: Branch) => {
    setBranch(nextBranch);
    setOwnerVehicleId(undefined);
  };

  const handleFailure = (error: unknown, setErrors: SetErrors) =>
    match(getCommitFailure(error))
      .with({kind: 'factory-reference-in-use'}, (nextFailure) => {
        setFailure(undefined);
        setOwnerVehicleId(nextFailure.vehicleId);
        setErrors([
          {
            name: 'importerOrderRef',
            message: getFactoryReferenceInUseMessage(),
          },
        ]);
      })
      .with({kind: Pattern.union('conflict', 'already-ordered')}, (nextFailure) => {
        setFailure(nextFailure);
        void detailQuery.refetch();
      })
      .with({kind: 'price-expired'}, () => {
        setFailure(undefined);
        void detailQuery.refetch();
      })
      .with({kind: 'business-case-already-selected'}, (nextFailure) => {
        setFailure(nextFailure);
        dispatch(newVehicleApi.util.invalidateTags(['NewVehicleConfiguration']));
        if (businessCaseId) {
          refreshBusinessCase(businessCaseId);
        }
      })
      .with({kind: Pattern.union('api-error', 'unknown')}, () => {
        setFailure(undefined);
        if (isApiError(error)) {
          handleApiError(error);
        } else {
          showNotification.error(
            t('entity.newVehicle.notifications.orderFailed', 'Vehicle order could not be created')
          );
        }
      })
      .otherwise((nextFailure) => setFailure(nextFailure));

  const commitToBusinessCase = (values: ContractValues, setErrors: SetErrors) => {
    if (!canOrder || !businessCaseId || orderBlocked) {
      return Promise.resolve();
    }
    setFailure(undefined);
    setOwnerVehicleId(undefined);
    const commitment = {
      businessCaseId,
      version: detail.configuration.version,
      catalogueRevision: detail.currentPriceSnapshot.catalogueRevision,
      importerOrderRef: toImporterOrderRef(values.importerOrderRef),
    };
    return commit({
      configurationId,
      commitConfigurationToBusinessCaseRequestBody: props.replacement
        ? {
            ...commitment,
            replaceSaleVehicle: true,
            expectedSaleVehicleId: props.replacement.expectedSaleVehicleId,
          }
        : commitment,
    })
      .unwrap()
      .then(({vehicleId}) => onSuccess(vehicleId, businessCaseId))
      .catch((error: unknown) => handleFailure(error, setErrors));
  };

  const confirmContract = (values: ContractValues, setErrors: SetErrors) => {
    if (isNil(chosenAlternative) || chosenAlternative.configurationId === configurationId) {
      return commitToBusinessCase(values, setErrors);
    }
    const alternativeLabel = (alternative: {name: string | null; letter: string}) =>
      alternative.name ??
      i18n.t('entity.offerAlternative.label', {
        defaultValue: 'Alternative {letter}',
        letter: alternative.letter,
      });
    const offered = offerAlternatives?.alternatives.find(
      (alternative) => alternative.configurationId === configurationId
    );
    openConfirmDialog({
      text: i18n.t('entity.offerAlternative.applyOverChoiceConfirm', {
        defaultValue: 'The customer chose {chosen}. Use {label} for checkout anyway?',
        chosen: alternativeLabel(chosenAlternative),
        label: offered ? alternativeLabel(offered) : detail.configuration.label,
      }),
      onConfirm: () => void commitToBusinessCase(values, setErrors),
    });
    return Promise.resolve();
  };

  const submitStock = (values: StockValues, setErrors: SetErrors) => {
    if (!canOrder || orderBlocked) {
      return Promise.resolve();
    }
    setFailure(undefined);
    setOwnerVehicleId(undefined);
    return order({
      configurationId,
      orderConfigurationRequestBody: {
        riskConfirmed: true,
        importerOrderRef: toImporterOrderRef(values.importerOrderRef),
        orderedOn: values.orderedOn || null,
        eta: values.eta || null,
        specFreezeDate: values.specFreezeDate || null,
        version: detail.configuration.version,
      },
    })
      .unwrap()
      .then(({vehicleId}) => onSuccess(vehicleId))
      .catch((error: unknown) => handleFailure(error, setErrors));
  };

  const repriceConfiguration = () => {
    if (
      !canWrite ||
      !isExpired ||
      catalogueChanged ||
      repriceConflictVersion === detail.configuration.version
    ) {
      return;
    }
    const submittedVersion = detail.configuration.version;
    setFailure(undefined);
    void reprice({
      configurationId,
      repriceConfigurationRequestBody: {
        version: detail.configuration.version,
        catalogueRevision: detail.currentPriceSnapshot.catalogueRevision,
      },
    })
      .unwrap()
      .then(() => detailQuery.refetch())
      .catch((error: unknown) => {
        if (isNewVehicleConflict(error)) {
          setFailure({kind: 'conflict'});
          setRepriceConflictVersion(submittedVersion);
          void detailQuery.refetch();
        } else if (isApiError(error)) {
          handleApiError(error);
        } else {
          showNotification.error(
            t('entity.newVehicle.notifications.orderFailed', 'Vehicle order could not be created')
          );
        }
      });
  };

  const failureAlert = getCommitFailureAlert(failure);
  const showConflict = failure?.kind === 'conflict' || failure?.kind === 'review-conflict';

  return (
    <VStack spacing={4}>
      <Text color="secondary">
        {t(
          'entity.newVehicle.order.intro',
          'Choose how this saved configuration should become an ordered vehicle.'
        )}
      </Text>
      <Show when={showConflict}>
        <Alert
          layout="detailed"
          variant="warning"
          message={t(
            'entity.newVehicle.order.conflict',
            'This configuration changed. Review the refreshed details and try again.'
          )}
          actions={[
            {
              title: t('entity.newVehicle.labels.reviewChanges', 'Review catalogue changes'),
              onClick: reviewConfiguration,
              isDisabled: !canWrite,
            },
          ]}
        />
      </Show>
      <Show when={failureAlert}>
        {(failureAlert) => <Alert variant={failureAlert.variant} message={failureAlert.message} />}
      </Show>
      <Show when={catalogueChanged}>
        <Alert
          layout="detailed"
          variant="warning"
          message={t(
            'entity.newVehicle.notifications.catalogueChanged',
            'The catalogue changed after this price was saved'
          )}
          actions={[
            {
              title: t('entity.newVehicle.labels.reviewChanges', 'Review catalogue changes'),
              onClick: reviewConfiguration,
              isDisabled: !canWrite,
            },
          ]}
        />
      </Show>
      <Show when={isExpired && !catalogueChanged}>
        <Alert
          layout="detailed"
          variant="error"
          message={t('entity.newVehicle.order.priceExpired', 'Price expired — re-price first')}
          actions={[
            {
              title: t('entity.newVehicle.actions.reprice', 'Re-price'),
              onClick: repriceConfiguration,
              isDisabled: !canWrite || repriceConflictVersion === detail.configuration.version,
              isLoading: repriceState.isLoading,
            },
          ]}
        />
      </Show>
      <SelectableCard
        title={t('entity.newVehicle.order.confirmContract', 'Confirm for contract')}
        description={t(
          'entity.newVehicle.order.confirmContractDescription',
          'Use the linked Business Case and create the customer vehicle order.'
        )}
        control="radio"
        name="order-branch"
        isSelected={branch === 'contract'}
        isDisabled={!businessCaseId || orderBlocked}
        reason={match({
          canOrder: Boolean(canOrder),
          businessCaseId: Boolean(businessCaseId),
          catalogueChanged,
          isExpired,
        })
          .with({canOrder: false}, () => orderPermissionReason)
          .with({businessCaseId: false}, () =>
            t('entity.newVehicle.order.noBusinessCase', 'No business case linked')
          )
          .with({catalogueChanged: true}, () =>
            t('entity.newVehicle.labels.reviewChanges', 'Review catalogue changes')
          )
          .with({isExpired: true}, () =>
            t('entity.newVehicle.order.priceExpired', 'Price expired — re-price first')
          )
          .otherwise(() => undefined)}
        footer={
          businessCaseId ? undefined : (
            <VStack spacing={2} align="flex-start">
              <Text size="small" color="danger">
                {t('entity.newVehicle.order.noBusinessCase', 'No business case linked')}
              </Text>
              <Text size="small" color="secondary">
                {t(
                  'entity.newVehicle.order.noBusinessCaseRemedy',
                  'Open the customer’s Business Case and use Select existing configuration to order this one for them.'
                )}
              </Text>
              <Button
                variant="link"
                size="small"
                rightIcon="action/open_in_new"
                title={t('entity.newVehicle.order.openBusinessCases', 'Open Business Cases')}
                onClick={() => {
                  closeDialog(dialogId);
                  navigate(businessCaseRoutes.home);
                }}
              />
            </VStack>
          )
        }
        onSelect={() => selectBranch('contract')}
      />
      <SelectableCard
        title={t('entity.newVehicle.order.dealerStock', 'Dealer stock order')}
        description={t(
          'entity.newVehicle.order.dealerStockDescription',
          'Order without a customer contract and accept the dealer stock risk.'
        )}
        control="radio"
        name="order-branch"
        isSelected={branch === 'stock'}
        isDisabled={orderBlocked}
        reason={match({canOrder: Boolean(canOrder), catalogueChanged, isExpired})
          .with({canOrder: false}, () => orderPermissionReason)
          .with({catalogueChanged: true}, () =>
            t('entity.newVehicle.labels.reviewChanges', 'Review catalogue changes')
          )
          .with({isExpired: true}, () =>
            t('entity.newVehicle.order.priceExpired', 'Price expired — re-price first')
          )
          .otherwise(() => undefined)}
        onSelect={() => selectBranch('stock')}
      />
      {match(branch)
        .with('contract', () => (
          <Form
            key="contract"
            experimentalZodSchema={contractSchema}
            defaultValues={{importerOrderRef: ''}}
            onSubmit={confirmContract}
          >
            {(control) => (
              <VStack spacing={4}>
                <Grid columns={[1, 2]}>
                  <FormField
                    control={control}
                    name="importerOrderRef"
                    type="text"
                    label={t(
                      'entity.newVehicle.order.importerReference',
                      'Importer order reference'
                    )}
                    onChange={() => setOwnerVehicleId(undefined)}
                  />
                </Grid>
                <Show when={ownerVehicleId}>
                  {(vehicleId) => <FactoryReferenceOwnerLink vehicleId={vehicleId} />}
                </Show>
                <HStack justify="flex-end">
                  <FormButton
                    control={control}
                    type="submit"
                    variant="primary"
                    title={t('entity.newVehicle.order.confirmContract', 'Confirm for contract')}
                    isDisabled={!businessCaseId || orderBlocked}
                    isLoading={commitState.isLoading}
                  />
                </HStack>
              </VStack>
            )}
          </Form>
        ))
        .with('stock', () => (
          <Form
            key="stock"
            experimentalZodSchema={stockSchema}
            defaultValues={{
              riskConfirmed: false,
              importerOrderRef: '',
              orderedOn: null,
              eta: null,
              specFreezeDate: null,
            }}
            onSubmit={submitStock}
          >
            {(control) => (
              <VStack spacing={4}>
                <FormField
                  control={control}
                  name="riskConfirmed"
                  type="checkbox"
                  label={t(
                    'entity.newVehicle.order.riskConfirmed',
                    'I confirm the dealer stock risk'
                  )}
                  isRequired
                />
                <Grid columns={[1, 2]}>
                  <FormField
                    control={control}
                    name="importerOrderRef"
                    type="text"
                    label={t(
                      'entity.newVehicle.order.importerReference',
                      'Importer order reference'
                    )}
                    onChange={() => setOwnerVehicleId(undefined)}
                  />
                  <FormField
                    control={control}
                    name="orderedOn"
                    type="apiDate"
                    label={t('entity.newVehicle.order.orderedOn', 'Ordered on')}
                  />
                  <FormField
                    control={control}
                    name="eta"
                    type="apiDate"
                    label={t('entity.newVehicle.order.eta', 'Estimated arrival')}
                  />
                  <FormField
                    control={control}
                    name="specFreezeDate"
                    type="apiDate"
                    label={t('entity.newVehicle.order.specFreezeDate', 'Specification freeze date')}
                  />
                </Grid>
                <Show when={ownerVehicleId}>
                  {(vehicleId) => <FactoryReferenceOwnerLink vehicleId={vehicleId} />}
                </Show>
                <HStack justify="flex-end">
                  <FormButton
                    control={control}
                    type="submit"
                    title={t('entity.newVehicle.order.placeStockOrder', 'Place stock order')}
                    isDisabled={orderBlocked}
                    isLoading={orderState.isLoading}
                  />
                </HStack>
              </VStack>
            )}
          </Form>
        ))
        .exhaustive()}
    </VStack>
  );
}
